import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { GripVertical, Pencil, Plus, Trash2, Video, FileQuestion } from "lucide-react";
import { LessonDialog } from "@/components/t4r/CourseDialogs";
import { QuizDialog } from "@/components/t4r/QuizDialog";

const emptyLesson = { title: "", body_markdown: "", video_storage_path: "", video_filename: "", video_external_url: "", order: 10, video_checkpoints: [], video_pass_mark: null };

const LessonRow = ({ l, i, dragging, onDragStart, onDragOver, onDrop, onEdit, onQuiz, onDelete }) => (
  <div draggable onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop}
    className={`flex items-center gap-3 rounded-lg border bg-white p-3 transition-colors ${dragging ? "border-green-400 opacity-50" : "border-border hover:border-green-200"}`} data-testid={`lesson-row-${l.id}`}>
    <GripVertical size={16} className="cursor-grab text-slate-400" data-testid={`lesson-drag-${l.id}`} />
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-50 text-xs font-semibold text-green-800">{i + 1}</span>
    <div className="min-w-0 flex-1">
      <p className="truncate font-medium">{l.title}</p>
      <p className="flex gap-3 text-[11px] text-muted-foreground">
        <span>{l.body_markdown ? `${l.body_markdown.split(/\s+/).length} words` : "No content yet"}</span>
        {(l.video_external_url || l.video_storage_path) && <span className="inline-flex items-center gap-1"><Video size={10} /> video</span>}
        {l.video_checkpoints?.length > 0 && <span className="font-medium text-green-700" data-testid={`lesson-interactive-${l.id}`}>interactive · {l.video_checkpoints.length} question{l.video_checkpoints.length === 1 ? "" : "s"}{l.video_pass_mark ? ` · pass ${l.video_pass_mark}/${l.video_checkpoints.length}` : ""}</span>}
      </p>
    </div>
    <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={onQuiz} data-testid={`lesson-quiz-${l.id}`}><FileQuestion size={11} /> Quiz</Button>
    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onEdit} data-testid={`lesson-edit-${l.id}`}><Pencil size={11} /></Button>
    <Button size="sm" variant="outline" className="h-7 border-rose-200 text-xs text-rose-600" onClick={onDelete} data-testid={`lesson-delete-${l.id}`}><Trash2 size={11} /></Button>
  </div>
);

export const CourseLessonsTab = ({ course, reload }) => {
  const [lessons, setLessons] = useState([]);
  const [form, setForm] = useState(emptyLesson);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [quizId, setQuizId] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);

  const load = useCallback(() => api.get(`/lms/admin/courses/${course.id}/lessons`).then(({ data }) => setLessons(data.lessons || [])), [course.id]);
  useEffect(() => { load(); }, [load]);

  const drop = async (to) => {
    if (dragIdx === null || dragIdx === to) { setDragIdx(null); return; }
    const next = [...lessons]; const [moved] = next.splice(dragIdx, 1); next.splice(to, 0, moved);
    setLessons(next); setDragIdx(null);
    try { await api.put(`/lms/admin/courses/${course.id}/lessons/reorder`, { lesson_ids: next.map((l) => l.id) }); toast.success("Lesson order saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save order"); load(); }
  };
  const save = async () => {
    if (!form.title.trim()) { toast.error("Give the lesson a title"); return; }
    try {
      const payload = { ...form, course_id: course.id };
      if (editing) { await api.put(`/lms/admin/lessons/${editing.id}`, payload); toast.success("Lesson saved"); setOpen(false); }
      else { const { data } = await api.post("/lms/admin/lessons", payload); setEditing(data); toast.success("Lesson created — you can now add a video and materials"); }
      load(); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Delete lesson "${l.title}" and its quiz?`)) return;
    try { await api.delete(`/lms/admin/lessons/${l.id}`); toast.success("Lesson deleted"); load(); reload(); } catch { toast.error("Failed"); }
  };
  const edit = (l) => { setEditing(l); setForm({ title: l.title, body_markdown: l.body_markdown, video_storage_path: l.video_storage_path, video_filename: l.video_filename || "", video_external_url: l.video_external_url, order: l.order, video_checkpoints: l.video_checkpoints || [], video_pass_mark: l.video_pass_mark ?? null }); setOpen(true); };
  const create = () => { setEditing(null); setForm({ ...emptyLesson, order: (lessons[lessons.length - 1]?.order || 0) + 10 }); setOpen(true); };

  return (
    <div className="space-y-3" data-testid="course-lessons-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Drag the handle to change the order learners see. Changes save straight away.</p>
        <Button size="sm" className="gap-1 bg-green-700 text-white hover:bg-green-800" onClick={create} data-testid="new-lesson-btn"><Plus size={12} /> New lesson</Button>
      </div>
      {lessons.map((l, i) => (
        <LessonRow key={l.id} l={l} i={i} dragging={dragIdx === i}
          onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => drop(i)}
          onEdit={() => edit(l)} onQuiz={() => setQuizId(l.id)} onDelete={() => remove(l)} />
      ))}
      {lessons.length === 0 && <p className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No lessons yet — add your first lesson.</p>}
      <LessonDialog open={open} onOpenChange={setOpen} editing={editing} form={form} setForm={setForm} onSave={save} />
      <QuizDialog lessonId={quizId} onClose={() => setQuizId(null)} />
    </div>
  );
};
